/**
 * PerfHUD — Lightweight performance overlay.
 *
 * Tracks FPS, frame time, draw calls, triangles, entity count.
 * Rendered as DOM overlay (no canvas overhead).
 *
 * Optionally displays GPU per-pass timing when a GpuTimingSource is attached
 * via `setGpuProfiler()`.  When timestamp-query is unavailable, a single
 * "GPU timing unavailable" indicator is shown instead.
 */

import type { GpuTimingSource } from '../render/timing/GpuTimingSource.js';

export interface PerfSnapshot {
  fps: number;
  frameTime: number;
  drawCalls: number;
  triangles: number;
  entities: number;
}

export class PerfHUD {
  private container: HTMLDivElement;
  private visible = false;
  private frames = 0;
  private lastTime = performance.now();
  private fps = 0;
  private _gpuProfiler: GpuTimingSource | null = null;

  constructor(parent: HTMLElement = document.body) {
    this.container = document.createElement('div');
    this.container.style.cssText = `
      position: absolute; top: 10px; right: 10px;
      background: rgba(0,0,0,0.75); color: #0f0;
      font: 12px monospace; padding: 8px 12px;
      border-radius: 4px; pointer-events: none;
      z-index: 10000; display: none;
      line-height: 1.5; white-space: pre;
    `;
    parent.appendChild(this.container);
  }

  /** Attach any GPU timing source; a GPU section appears on every update. */
  setGpuProfiler(profiler: GpuTimingSource): void {
    this._gpuProfiler = profiler;
  }

  toggle(): void {
    this.visible = !this.visible;
    this.container.style.display = this.visible ? 'block' : 'none';
  }

  show(): void {
    this.visible = true;
    this.container.style.display = 'block';
  }

  hide(): void {
    this.visible = false;
    this.container.style.display = 'none';
  }

  update(snapshot: Partial<PerfSnapshot>): void {
    if (!this.visible) return;

    this.frames++;
    const now = performance.now();
    const elapsed = now - this.lastTime;
    if (elapsed >= 1000) {
      this.fps = Math.round((this.frames * 1000) / elapsed);
      this.frames = 0;
      this.lastTime = now;
    }

    const lines = [
      `FPS: ${this.fps}`,
      `Frame: ${snapshot.frameTime?.toFixed(1) ?? '?'}ms`,
    ];
    if (snapshot.drawCalls !== undefined) lines.push(`Draw: ${snapshot.drawCalls}`);
    if (snapshot.triangles !== undefined) lines.push(`Tris: ${formatNumber(snapshot.triangles)}`);
    if (snapshot.entities !== undefined) lines.push(`Ents: ${snapshot.entities}`);

    // GPU timing section
    if (this._gpuProfiler) {
      lines.push('--- GPU passes ---');
      if (!this._gpuProfiler.available) {
        lines.push('GPU timing unavailable');
      } else {
        const gpuStats = this._gpuProfiler.getFrameStats();
        if (gpuStats.size === 0) {
          lines.push('(no GPU data yet)');
        } else {
          const sorted = Array.from(gpuStats.entries())
            .sort((a, b) => b[1].meanMs - a[1].meanMs);
          for (const [name, s] of sorted) {
            const bar = _sparkBar(s.history, s.maxMs, 8);
            lines.push(`${name}: ${s.meanMs.toFixed(2)}ms ${bar}`);
          }
        }
      }
    }

    this.container.textContent = lines.join('\n');
  }

  dispose(): void {
    this.container.remove();
  }
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toString();
}

/** Build a compact ASCII sparkline bar from a history array. */
function _sparkBar(history: number[], maxVal: number, width: number): string {
  if (history.length === 0 || maxVal <= 0) return '';
  const bars = ' ▁▂▃▄▅▆▇█';
  const slice = history.slice(-width);
  return slice.map((v) => {
    const normalized = Math.min(1, v / maxVal);
    const idx = Math.round(normalized * (bars.length - 1));
    return bars[idx];
  }).join('');
}
